import { memo } from "react"
import { Disclaimer } from "@/components/resources/components/Disclaimer"
import { ArticleHero } from "@/components/resources/components/Hero"
import {
  SanityContentRenderer,
  type SanityContentRendererProps,
} from "@/components/resources/components/SanityComponents/SanityContentRenderer"
import { ArticleSchema } from "@/components/resources/components/Schemas"
import { TableOfContentsLayout } from "@/components/resources/components/TableOfContents"
import { TeaserGrid } from "@/components/resources/components/TeaserGrid"
import { NewsletterTeaser } from "@/components/resources/components/TeaserGrid/NewsletterTeaser"
import { ScrollPageProvider } from "@/components/resources/context/ScrollPage/ScrollPageContext"
import type { Article } from "@/components/resources/data/sanity/posts"

const ArticleContent = memo(
  ({ layoutSizes, ...props }: SanityContentRendererProps) => (
    <>
      <div className="max-w-3xl mx-auto px-4">
        <SanityContentRenderer {...props} layoutSizes={layoutSizes} />
        <Disclaimer />
      </div>
      <div className="flex justify-center mb-8 md:mb-12 lg:mb-16 pt-8 md:pt-12 lg:pt-16 max-w-5xl mx-auto px-4">
        <NewsletterTeaser as="section" />
      </div>
    </>
  ),
  () => true,
) // No need to re-render this even if possible parent layout re-renders

ArticleContent.displayName = "ArticleContentRenderer"

export const ArticlePage = (article: Article) => {
  const {
    title,
    subtitle,
    body,
    categoryName,
    image,
    author,
    publishedAt,
    related,
    category,
    releaseNumber,
    projectMeta,
    tableOfContents,
  } = article
  return (
    <article className="mb-8 md:mb-12 lg:mb-16">
      <div className="max-w-7xl mx-auto px-4">
        <ArticleHero
          categoryName={categoryName}
          category={category}
          title={title}
          subtitle={subtitle}
          image={image}
          publishedAt={publishedAt}
          releaseNumber={releaseNumber}
          projectMeta={projectMeta}
          author={author}
        />
      </div>
      {tableOfContents ? (
        <ScrollPageProvider scrollOffset={100}>
          <TableOfContentsLayout tableOfContents={tableOfContents}>
            <ArticleContent
              content={body}
              layoutSizes={{ wide: "wide-content", normal: "content" }}
            />
          </TableOfContentsLayout>
        </ScrollPageProvider>
      ) : (
        <ArticleContent content={body} />
      )}
      {related?.length ? (
        <section className="pt-8 md:pt-12 lg:pt-16 mb-8 md:mb-12 lg:mb-16 max-w-7xl mx-auto px-4">
          <div className="flex flex-col gap-6">
            <h2 className="text-xl font-medium">Continue reading</h2>
            <TeaserGrid teasers={related} />
          </div>
        </section>
      ) : null}
      <ArticleSchema article={article} />
    </article>
  )
}
